import { makeStyles } from '@material-ui/core/styles';

export const useInsightsStyles = makeStyles(theme => ({
  page: {
    width: '100%',
    minWidth: 0,
    color: 'var(--dm-text-strong, #263548)',
    fontVariantNumeric: 'tabular-nums',
  },
  loadingOverlay: { opacity: 0.58, pointerEvents: 'none', transition: 'opacity 180ms ease-out' },
  progress: { position: 'sticky', top: 0, zIndex: 20, margin: theme.spacing(-2, -2, 2) },
  section: {
    marginTop: theme.spacing(3),
    borderRadius: 16,
    border: '1px solid var(--dm-border, #e7ebf0)',
    background: 'var(--contentColor, #fff)',
    boxShadow: '0 8px 24px rgba(12, 41, 84, 0.055)',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
      borderRadius: 12,
    },
  },
  panel: {
    position: 'relative',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: '1px solid var(--dm-border, #e7ebf0)',
    background: 'var(--contentColor, #fff)',
    boxShadow: '0 8px 24px rgba(12, 41, 84, 0.055)',
    color: 'var(--dm-text-strong, #263548)',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.5) },
  },
  sectionPanel: {
    border: 0,
    borderRadius: 0,
    background: 'transparent',
    boxShadow: 'none',
  },
  sectionTitle: { fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.35, [theme.breakpoints.down('xs')]: { fontSize: '1rem' } },
  sectionSubtitle: { color: 'var(--dm-muted, #647184)', fontSize: '0.8125rem', marginTop: 4, lineHeight: 1.5 },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: 0,
    padding: theme.spacing(2, 2.5, 0),
    boxSizing: 'border-box',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 1.5, 0),
      gap: theme.spacing(1.25),
    },
  },
  sectionActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'flex-end',
      '& .MuiButton-root': { minHeight: 36 },
    },
  },
  metricValue: { fontSize: '1.75rem', fontWeight: 750, letterSpacing: '-0.025em', lineHeight: 1.2, overflowWrap: 'anywhere', [theme.breakpoints.down('xs')]: { fontSize: '1.5rem' } },
  muted: { color: 'var(--dm-muted, #647184)' },
  description: { color: 'var(--dm-muted, #647184)', fontSize: '0.75rem', lineHeight: 1.45, marginTop: theme.spacing(0.75) },
  tile: {
    boxSizing: 'border-box', padding: theme.spacing(1.75), borderRadius: 12,
    background: 'var(--dm-row-alt, #f8fafc)', border: '1px solid var(--dm-border, #e7ebf0)',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.25) },
  },
  tableWrap: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain',
    scrollbarWidth: 'thin',
    border: '1px solid var(--dm-border, #e7ebf0)',
    borderRadius: 12,
    '&:focus': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    [theme.breakpoints.down('xs')]: { borderRadius: 10 },
  },
  table: {
    '& .MuiTableCell-root': { borderBottomColor: 'var(--dm-border, #e7ebf0)', padding: theme.spacing(1.25, 1.5), verticalAlign: 'top' },
    '& .MuiTableCell-head': { background: 'var(--dm-row-alt, #f8fafc)', color: 'var(--dm-text-strong, #263548)', fontWeight: 700, whiteSpace: 'nowrap' },
    '& .MuiTableRow-root:last-child .MuiTableCell-body': { borderBottom: 0 },
    [theme.breakpoints.down('xs')]: {
      '& .MuiTableCell-root': { padding: theme.spacing(1, 1.25), fontSize: '0.72rem', lineHeight: 1.4 },
    },
  },
  clickableRow: { cursor: 'pointer', '&:hover': { background: 'var(--dm-hover, rgba(0,0,0,.04))' }, '&:focus': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 } },
  footnote: { color: 'var(--dm-muted, #647184)', fontSize: '0.72rem', lineHeight: 1.5, marginTop: theme.spacing(1.25) },
  callout: { display: 'flex', gap: theme.spacing(1.25), padding: theme.spacing(1.5), borderRadius: 12, marginBottom: theme.spacing(2), border: '1px solid rgba(245, 158, 11, .38)', background: 'rgba(245, 158, 11, .09)', [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.25), gap: theme.spacing(1) } },
  chart: { position: 'relative', width: '100%', height: 300, marginTop: theme.spacing(1), [theme.breakpoints.down('xs')]: { height: 240 } },
  empty: {
    padding: theme.spacing(2), borderRadius: 12, border: '1px dashed var(--dm-border, #d5dde8)',
    color: 'var(--dm-muted, #647184)', fontSize: '.82rem', lineHeight: 1.55, textAlign: 'center',
  },
  subheading: { fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.35, margin: theme.spacing(3, 0, 1.25), [theme.breakpoints.down('xs')]: { margin: theme.spacing(2, 0, 1) } },
  splitCards: { marginTop: theme.spacing(2) },
  srOnly: {
    border: 0, clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', height: 1, margin: -1, overflow: 'hidden',
    padding: 0, position: 'absolute', whiteSpace: 'nowrap', width: 1,
  },
  '@global': {
    '@media (prefers-reduced-motion: reduce)': {
      '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
    },
  },
}));
